import React, { useRef, useEffect, useLayoutEffect, useCallback, useMemo } from 'react';
import ViewerJs from 'viewerjs';
import { ImageProps } from './iImage';
import { getNid, Warning, WebUI } from "../../wui-core/src";
import cx from 'classnames';
import Icon from '../../wui-icon/src';
import i18n from './i18n.js';
import { getLangInfo } from '../../wui-locale/src/tool';
import { WithConfigConsumer } from '../../wui-provider/src/context';

const { isShouldUpdate } = Warning;

const defaultProps: Partial<ImageProps> = {
    ready: () => {
    },
    show: () => {
    },
    shown: () => {
    },
    hide: () => {
    },
    hidden: () => {
    },
    view: () => {
    },
    viewed: () => {
    },
    zoom: () => {
    },
    zoomed: () => {
    },
    asyncLoad: false,
    toolbar: true,
};

const Viewer: React.FC<ImageProps> = (props) => {
    const viewerCaseRef = useRef<ViewerJs | undefined>(undefined);
    const viewsRef = useRef<HTMLDivElement | null>(null);

    const langMap = useMemo(() => {
        return getLangInfo(props.locale, i18n, 'image').langMap;
    }, [props.locale]);

    const toolbarConfig = useMemo(() => {
        return {
            prev: { show: true, title: langMap.prev },
            prevLeft: { show: true, title: langMap.prev },
            number: { show: true },
            next: { show: true, title: langMap.next },
            nextRight: { show: true, title: langMap.next },
            zoomOut: { show: true, title: langMap.zoomOut },
            zoomIn: { show: true, title: langMap.zoomIn },
            fullscreen: { show: true, title: langMap.fullscreen },
            rotateLeft: { show: props.showAllToolbar || false, title: langMap.rotateLeft },
            rotateRight: { show: true, title: langMap.rotateRight },
            flipHorizontal: { show: true, title: langMap.flipHorizontal },
            flipVertical: { show: props.showAllToolbar || false, title: langMap.flipVertical },
            reset: { show: true, title: langMap.reset },
            play: { show: true, title: langMap.play },
        };
    }, [props.showAllToolbar, langMap]);

    const onMouseEnter = useCallback((event: MouseEvent) => {
        const { dir, clsPrefix } = props;
        const mask = viewsRef.current?.querySelector(`.${clsPrefix}-mask`) as HTMLDivElement;
        const image = event.target as HTMLImageElement;
        if (mask) {
            mask.style[dir === 'rtl' ? "right" : "left"] = dir === 'rtl' ?
                Number(viewsRef.current?.offsetWidth) - image.offsetLeft - image.offsetWidth + 'px' :
                image.offsetLeft + image.offsetWidth / 2 + 'px';
            mask.style.top = image.offsetTop + image.offsetHeight / 2 + 'px';
            mask.style.display = 'block';
        }
    }, [props.dir, props.clsPrefix]);

    const onMouseLeave = useCallback(() => {
        const { clsPrefix } = props;
        const mask = viewsRef.current?.querySelector(`.${clsPrefix}-mask`) as HTMLDivElement;
        if (mask) {
            mask.style.display = 'none';
        }
    }, [props.clsPrefix]);

    const addHandlers = useCallback(() => {
        viewsRef.current?.querySelectorAll('img')?.forEach((img) => {
            img?.addEventListener('mouseenter', onMouseEnter);
            img?.addEventListener('mouseleave', onMouseLeave);
        });
    }, [onMouseEnter, onMouseLeave]);

    const removeHandlers = useCallback(() => {
        viewsRef.current?.querySelectorAll('img')?.forEach((img) => {
            img?.removeEventListener('mouseenter', onMouseEnter);
            img?.removeEventListener('mouseleave', onMouseLeave);
        });
    }, [onMouseEnter, onMouseLeave]);

    const initViewer = useCallback(() => {
        const { container, getPopupContainer, viewed, toolbar, dir, ...other } = props;
        const containerDom = getPopupContainer || container;
        const className = cx({
            [`viewer-rtl`]: dir === 'rtl',
        }, props.className);

        if (viewsRef.current) {
            viewerCaseRef.current = new ViewerJs(viewsRef.current, {
                url: 'data-original',
                className,
                container: containerDom,
                rtl: dir === 'rtl',
                viewed: (arg) => {
                    if (arg?.detail?.originalImage?.getAttribute('fieldid')) {
                        arg?.detail?.image?.setAttribute('fieldid', arg?.detail?.originalImage?.getAttribute('fieldid') + '_max');
                    }
                    viewed && viewed();
                },
                toolbar: toolbar === true ? toolbarConfig : toolbar,
                ...other
            });
            addHandlers();
        }
    }, [props, toolbarConfig, addHandlers]);

    useLayoutEffect(() => {
        // 使用 useLayoutEffect 在 DOM 更新后同步执行
        if (!props.asyncLoad && viewsRef.current) {
            initViewer();
        }

        return () => {
            removeHandlers();
            viewerCaseRef.current?.destroy?.();
        };
    }, []);

    useEffect(() => {
        // 处理 asyncLoad 情况和 props 更新
        if (props.asyncLoad && viewsRef.current) {
            // 延迟初始化以确保 DOM 已完全准备好
            const timer = setTimeout(() => {
                if (!viewerCaseRef.current) {
                    initViewer();
                } else {
                    viewerCaseRef.current.update();
                }
            }, 0);
            return () => clearTimeout(timer);
        }
    }, [props.children, props.asyncLoad, props.keyboard]);

    const {
        getPopupContainer,
        container,
        ready,
        show,
        shown,
        hide,
        hidden,
        view,
        viewed,
        zoom,
        zoomed,
        asyncLoad,
        clsPrefix,
        className,
        inline,
        button,
        title,
        navbar,
        toolbar,
        tooltip,
        movable,
        zoomable,
        rotatable,
        scalable,
        fullscreen,
        keyboard,
        interval,
        zoomRatio,
        minZoomRatio,
        maxZoomRatio,
        zIndex,
        zIndexInline,
        url,
        dir,
        ...other
    } = props;

    isShouldUpdate("Image", props);
    const adapterNid = getNid(props);
    const wrapClassNames = cx({
        [`${clsPrefix}`]: true,
        [`${clsPrefix}-rtl`]: dir === 'rtl',
    }, className);

    return (
        <div {...other} className={wrapClassNames} ref={viewsRef} {...adapterNid}>
            {props.children}
            <div className={`${clsPrefix}-mask`}>
                <Icon type="uf-eye" />
                <span>{langMap.preview}</span>
            </div>
        </div>
    );
};

export default WithConfigConsumer({ name: "image" })(
    WebUI({ name: "image", defaultProps })(Viewer)
) as React.ComponentClass<Partial<ImageProps>>;
